// Admin support queue (Story #4613, Stream 8c) — mockup 04. Filter row + table
// with Escalated / Duplicate / Security-review badges + a sync-status icon.
//
// NOTE (backend read-contract gaps, flagged in the story): the deployed admin
// serializer does not persist priority, assignee, or a requester display name, so
// those columns fall back to an honest "—" rather than fabricating a value.

import { useMemo, useState } from 'react'
import { AlertTriangle, RefreshCw, ShieldAlert, Zap } from 'lucide-react'
import type { RequestType } from '../../../types/support'
import type { AdminSupportTicket } from '../../../types/support'
import { REQUEST_TYPE_OPTIONS } from '../../../constants/support'
import { deriveDisplayStatus, relativeAge } from '../../../utils/supportDisplay'
import { DisplayStatusChip, PriorityBadge, RequestTypeBadge } from '../SupportBadges'

type StatusFilter = 'needs_action' | 'all_open' | 'waiting' | 'resolved' | 'everything'

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'needs_action', label: 'Needs action' },
  { value: 'all_open', label: 'All open' },
  { value: 'waiting', label: 'Waiting on user' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'everything', label: 'Everything' },
]

function isEscalated(t: AdminSupportTicket): boolean {
  return Boolean(t.last_escalated_at || t.escalated)
}
function needsSecurityReview(t: AdminSupportTicket): boolean {
  return (t.active_quarantine_count ?? 0) > 0
}

function SyncCell({ t }: { t: AdminSupportTicket }) {
  if (t.sync_status === 'failed') {
    return (
      <span className="inline-flex items-center gap-1 font-medium text-rose-600">
        <RefreshCw size={13} /> retry
      </span>
    )
  }
  if (t.ado_id) return <span className="text-emerald-700">✓ #{t.ado_id}</span>
  return <span className="text-slate-400">pending</span>
}

interface AdminQueueTableProps {
  tickets: AdminSupportTicket[]
  onOpen: (ticketId: string) => void
}

export default function AdminQueueTable({ tickets, onOpen }: AdminQueueTableProps) {
  const [status, setStatus] = useState<StatusFilter>('needs_action')
  const [type, setType] = useState<RequestType | 'all'>('all')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return tickets
      .map((t) => ({ t, ds: deriveDisplayStatus(t, t.user_id) }))
      .filter(({ t, ds }) => {
        switch (status) {
          case 'needs_action':
            return t.status === 'open' && ds !== 'waiting'
          case 'all_open':
            return t.status === 'open'
          case 'waiting':
            return ds === 'waiting'
          case 'resolved':
            return ds === 'resolved'
          case 'everything':
            return true
        }
      })
      .filter(({ t }) => (type === 'all' ? true : t.request_type === type))
      .filter(({ t }) =>
        needle ? `${t.ref} ${t.subject} ${t.user_id ?? ''}`.toLowerCase().includes(needle) : true,
      )
  }, [tickets, status, type, search])

  const openCount = tickets.filter((t) => t.status === 'open').length
  const unassigned = tickets.filter((t) => t.status === 'open' && !t.assignee).length
  const syncFailures = tickets.filter((t) => t.sync_status === 'failed').length

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div>
      <p className="mb-3 text-[13px] text-slate-500">
        {openCount} open · {unassigned} unassigned · {syncFailures} sync failure{syncFailures === 1 ? '' : 's'}
      </p>

      {/* Filters */}
      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-iflow-900/10 bg-white/70 px-3 py-2.5">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusFilter)}
          className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-[12.5px]"
        >
          {STATUS_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
        <select
          value={type}
          onChange={(e) => setType(e.target.value as RequestType | 'all')}
          className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-[12.5px]"
        >
          <option value="all">Type: All</option>
          {REQUEST_TYPE_OPTIONS.filter((o) => o.value !== 'question').map((o) => (
            <option key={o.value} value={o.backend}>
              {o.label}
            </option>
          ))}
        </select>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search ref, subject, requester…"
          className="min-w-[180px] flex-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-[12.5px]"
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-iflow-900/10">
        <table className="w-full min-w-[900px] border-collapse bg-white text-left text-[12.5px]">
          <thead>
            <tr className="bg-iflow-900 text-[11.5px] uppercase tracking-wide text-white">
              <th className="px-3 py-2.5" />
              <th className="px-3 py-2.5 font-semibold">Ref</th>
              <th className="px-3 py-2.5 font-semibold">Subject</th>
              <th className="px-3 py-2.5 font-semibold">Requester</th>
              <th className="px-3 py-2.5 font-semibold">Type</th>
              <th className="px-3 py-2.5 font-semibold">Priority</th>
              <th className="px-3 py-2.5 font-semibold">Status</th>
              <th className="px-3 py-2.5 font-semibold">Age</th>
              <th className="px-3 py-2.5 font-semibold">Assignee</th>
              <th className="px-3 py-2.5 font-semibold">ADO</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-3 py-10 text-center text-slate-500">
                  No tickets match these filters.
                </td>
              </tr>
            ) : (
              rows.map(({ t, ds }) => (
                <tr key={t.ticket_id} className="border-b border-slate-100 hover:bg-iflow-50">
                  <td className="px-3 py-2.5">
                    <input
                      type="checkbox"
                      checked={selected.has(t.ticket_id)}
                      onChange={() => toggle(t.ticket_id)}
                      aria-label={`Select ${t.ref}`}
                    />
                  </td>
                  <td className="px-3 py-2.5">
                    <button onClick={() => onOpen(t.ticket_id)} className="font-semibold text-iflow-600 hover:underline">
                      {t.ref}
                    </button>
                  </td>
                  <td className="max-w-[260px] truncate px-3 py-2.5 text-slate-800">{t.subject}</td>
                  <td className="px-3 py-2.5 text-slate-600">{t.requester_name || t.user_id || '—'}</td>
                  <td className="px-3 py-2.5">
                    <RequestTypeBadge type={t.request_type} />
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="flex flex-wrap items-center gap-1">
                      {t.priority ? <PriorityBadge priority={t.priority} /> : <span className="text-slate-400">—</span>}
                      {isEscalated(t) && (
                        <span className="inline-flex items-center gap-0.5 rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-semibold text-amber-900">
                          <Zap size={10} /> Escalated
                        </span>
                      )}
                      {t.duplicate_of && (
                        <span className="inline-flex items-center gap-0.5 rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-800">
                          <AlertTriangle size={10} /> Duplicate
                        </span>
                      )}
                      {needsSecurityReview(t) && (
                        <span className="inline-flex items-center gap-0.5 rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-800">
                          <ShieldAlert size={10} /> Security review
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <DisplayStatusChip status={ds} />
                  </td>
                  <td className="px-3 py-2.5 text-slate-500">{relativeAge(t.created_at)}</td>
                  <td className="px-3 py-2.5 text-slate-600">{t.assignee || '—'}</td>
                  <td className="px-3 py-2.5">
                    <SyncCell t={t} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Bulk bar — rendered to match mockup 04; bulk status/assign have no backend
          endpoint in this release, so the controls are disabled (honest, not fake). */}
      {selected.size > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2.5 rounded-xl border border-iflow-900/10 bg-white/70 px-3.5 py-2.5">
          <b className="text-[12.5px]">{selected.size} selected</b>
          <select disabled className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 text-[12.5px] text-slate-400">
            <option>Set status…</option>
          </select>
          <select disabled className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 text-[12.5px] text-slate-400">
            <option>Assign to…</option>
          </select>
          <button disabled className="rounded-lg bg-slate-200 px-3 py-1.5 text-[12.5px] font-semibold text-slate-400" title="Bulk actions aren’t available in this release">
            Apply
          </button>
          <span className="text-[11.5px] text-slate-400">Bulk actions aren’t available yet.</span>
        </div>
      )}
    </div>
  )
}
